import * as React from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Container,
  CssBaseline,
  Grid,
  Toolbar,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SchemaIcon from "@mui/icons-material/Schema";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import Navigation from "../components/Navigation";
import AdminPageHeader from "../components/AdminPageHeader";
import CreateOrganizationDialog from "../components/admin/CreateOrganizationDialog";
import { useApi } from "../hooks/useApi";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

export default function AdminOrganizations() {
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [pagination, setPagination] = React.useState({
    total: 0,
    limit: 20,
    offset: 0,
    page: 1,
  });
  const [schemaDialogOpen, setSchemaDialogOpen] = React.useState(false);
  const [schemaTableName, setSchemaTableName] = React.useState("");
  const [schemaData, setSchemaData] = React.useState(null);
  const [loadingSchema, setLoadingSchema] = React.useState(false);
  const [schemaError, setSchemaError] = React.useState(null);
  const [createOrgDialogOpen, setCreateOrgDialogOpen] = React.useState(false);
  const [newOrgFormData, setNewOrgFormData] = React.useState({
    organization_name: "",
    formation_state: "",
    organization_ein: "",
    is_active: true,
  });
  const [creatingOrg, setCreatingOrg] = React.useState(false);
  const [createError, setCreateError] = React.useState(null);
  const [sortBy, setSortBy] = React.useState("organization_name");
  const [sortOrder, setSortOrder] = React.useState("asc");

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["organizations", debouncedSearch, sortBy, sortOrder, pagination.offset, pagination.limit],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: pagination.limit.toString(),
        offset: pagination.offset.toString(),
        sortBy,
        sortOrder,
      });
      if (debouncedSearch?.trim()) params.append("search", debouncedSearch.trim());
      return api.get(`/admin/organizations?${params}`);
    },
  });

  const organizations = data?.data ?? [];
  const total = data?.pagination?.total ?? 0;
  const loading = isLoading;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const handlePageChange = (newPage) => {
    const newOffset = (newPage - 1) * pagination.limit;
    setPagination((prev) => ({ ...prev, offset: newOffset, page: newPage }));
  };

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setPagination((prev) => ({ ...prev, limit: newLimit, offset: 0, page: 1 }));
  };

  const handleSort = (field) => {
    const newOrder = sortBy === field && sortOrder === "asc" ? "desc" : "asc";
    setSortBy(field);
    setSortOrder(newOrder);
    setPagination((prev) => ({ ...prev, offset: 0, page: 1 }));
  };

  const totalPages = Math.ceil(total / pagination.limit);

  const handleCloseSchemaDialog = () => {
    setSchemaDialogOpen(false);
    setSchemaTableName("");
    setSchemaData(null);
    setSchemaError(null);
  };

  const handleCreateOrganization = async () => {
    if (!newOrgFormData.organization_name || !newOrgFormData.organization_name.trim()) {
      return;
    }

    setCreatingOrg(true);
    setCreateError(null);

    try {
      const orgData = {
        organization_name: newOrgFormData.organization_name.trim(),
        ...(newOrgFormData.formation_state?.trim() && { formation_state: newOrgFormData.formation_state.trim() }),
        ...(newOrgFormData.organization_ein?.trim() && { organization_ein: newOrgFormData.organization_ein.trim() }),
        is_active: newOrgFormData.is_active !== false,
      };

      const createdOrg = await api.post("/admin/organizations", orgData);

      // Close dialog and reset form
      setCreateOrgDialogOpen(false);
      setNewOrgFormData({
        organization_name: "",
        formation_state: "",
        organization_ein: "",
        is_active: true,
      });

      // Invalidate organizations cache to refresh list
      await queryClient.invalidateQueries({ queryKey: ["organizations"] });

      // Navigate to the new organization's detail page
      navigate(`/dashboard/organizations/${createdOrg.id}`);
    } catch (err) {
      console.error("Error creating organization:", err);
      setCreateError(err.message || "Failed to create organization");
    } finally {
      setCreatingOrg(false);
    }
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar sx={{ minHeight: "48px !important", height: "48px" }} />
          <Container maxWidth="xl" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  onClick={() => navigate("/dashboard")}
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Admin
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  Organizations
                </Typography>
              </Breadcrumbs>
            </Box>

            <AdminPageHeader
              title="Organizations"
              description="Manage all organizations in the system"
              icon={<BusinessIcon sx={{ color: "primary.main" }} />}
            >
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateOrgDialogOpen(true)}
                >
                  Create Organization
                </Button>
                <Button
                  variant="contained"
                  sx={{ bgcolor: "white", color: "black", "&:hover": { bgcolor: "grey.100" } }}
                  startIcon={<SchemaIcon />}
                  onClick={async () => {
                    setSchemaDialogOpen(true);
                    setSchemaTableName("organizations");
                    setSchemaData(null);
                    setSchemaError(null);
                    setLoadingSchema(true);
                    setSchemaError(null);
                    try {
                      const data = await api.get("/admin/schema/organizations");
                      setSchemaData(data);
                    } catch (err) {
                      console.error("Error fetching schema:", err);
                      setSchemaError(err.message || "Failed to fetch schema");
                    } finally {
                      setLoadingSchema(false);
                    }
                  }}
                >
                  Schema
                </Button>
              </Box>
            </AdminPageHeader>

            {(error || createError) && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {createError || error?.message || "Failed to fetch organizations"}
              </Alert>
            )}

            <Card>
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    placeholder="Search organizations by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {/* Top pagination controls */}
                {!loading && pagination.total > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        Showing {pagination.offset + 1}-
                        {Math.min(pagination.offset + pagination.limit, total)} of {total} organizations
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Per Page</InputLabel>
                        <Select
                          value={pagination.limit}
                          onChange={handleLimitChange}
                          label="Per Page"
                          disabled={loading}
                        >
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={20}>20</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                          <MenuItem value={100}>100</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    {totalPages > 1 && (
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={pagination.page <= 1 || loading}
                        >
                          Previous
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                          Page {pagination.page} of {totalPages} ({total} total)
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={pagination.page >= totalPages || loading}
                        >
                          Next
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}

                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : organizations.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography variant="h6" color="text.secondary">
                      No organizations found
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <TableSortLabel
                            active={sortBy === "organization_name"}
                            direction={sortBy === "organization_name" ? sortOrder : "asc"}
                            onClick={() => handleSort("organization_name")}
                          >
                            <Typography variant="subtitle2" fontWeight="bold">
                              Name
                            </Typography>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={sortBy === "formation_state"}
                            direction={sortBy === "formation_state" ? sortOrder : "asc"}
                            onClick={() => handleSort("formation_state")}
                          >
                            <Typography variant="subtitle2" fontWeight="bold">
                              State
                            </Typography>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={sortBy === "is_active"}
                            direction={sortBy === "is_active" ? sortOrder : "asc"}
                            onClick={() => handleSort("is_active")}
                          >
                            <Typography variant="subtitle2" fontWeight="bold">
                              Status
                            </Typography>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={sortBy === "created_at"}
                            direction={sortBy === "created_at" ? sortOrder : "asc"}
                            onClick={() => handleSort("created_at")}
                          >
                            <Typography variant="subtitle2" fontWeight="bold">
                              Created
                            </Typography>
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            ID
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="subtitle2" fontWeight="bold">
                            Actions
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {organizations.map((org) => (
                        <TableRow key={org.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {org.organization_name || org.account_name || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {org.state || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={org.is_active ? "Active" : "Inactive"}
                              color={org.is_active ? "success" : "default"}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(org.created_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.75rem",
                                color: "text.secondary",
                              }}
                            >
                              {org.id}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<VisibilityIcon />}
                              onClick={() => navigate(`/dashboard/organizations/${org.id}`)}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                    {/* Bottom pagination controls */}
                    {!loading && pagination.total > 0 && (
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                          <Typography variant="body2" color="text.secondary">
                            Showing {pagination.offset + 1}-
                            {Math.min(pagination.offset + pagination.limit, pagination.total)} of{" "}
                            {pagination.total} organizations
                          </Typography>
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Per Page</InputLabel>
                            <Select
                              value={pagination.limit}
                              onChange={handleLimitChange}
                              label="Per Page"
                              disabled={loading}
                            >
                              <MenuItem value={10}>10</MenuItem>
                              <MenuItem value={20}>20</MenuItem>
                              <MenuItem value={50}>50</MenuItem>
                              <MenuItem value={100}>100</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                        {totalPages > 1 && (
                          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handlePageChange(pagination.page - 1)}
                              disabled={pagination.page <= 1 || loading}
                            >
                              Previous
                            </Button>
                            <Typography variant="body2" color="text.secondary">
                              Page {pagination.page} of {totalPages} ({total} total)
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handlePageChange(pagination.page + 1)}
                              disabled={pagination.page >= totalPages || loading}
                            >
                              Next
                            </Button>
                          </Box>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Container>
        </Box>
      </Box>

      {/* Schema Dialog */}
      <Dialog
        open={schemaDialogOpen}
        onClose={handleCloseSchemaDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Table Schema</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Table Name</InputLabel>
              <Select
                value={schemaTableName}
                onChange={(e) => {
                  const tableName = e.target.value;
                  setSchemaTableName(tableName);
                  if (tableName) {
                    const fetchSchema = async () => {
                      setLoadingSchema(true);
                      setSchemaError(null);
                      setSchemaData(null);
                      try {
                        const data = await api.get(`/admin/schema/${tableName}`);
                        setSchemaData(data);
                      } catch (err) {
                        console.error("Error fetching schema:", err);
                        setSchemaError(err.message || "Failed to fetch schema");
                      } finally {
                        setLoadingSchema(false);
                      }
                    };
                    fetchSchema();
                  }
                }}
                label="Table Name"
              >
                <MenuItem value="organizations">organizations</MenuItem>
                <MenuItem value="users">users</MenuItem>
                <MenuItem value="subscriptions">subscriptions</MenuItem>
                <MenuItem value="orders">orders</MenuItem>
                <MenuItem value="products">products</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {schemaError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {schemaError}
            </Alert>
          )}

          {loadingSchema && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {schemaData && (
            <Box>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Table: {schemaData.table_name}
              </Typography>
              
              {/* Columns */}
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                Columns
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Nullable</TableCell>
                      <TableCell>Default</TableCell>
                      <TableCell>Position</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {schemaData.columns.map((col) => (
                      <TableRow key={col.name}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={schemaData.primary_keys?.includes(col.name) ? "bold" : "normal"}>
                            {col.name}
                            {schemaData.primary_keys?.includes(col.name) && " (PK)"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {col.type}
                            {col.max_length && `(${col.max_length})`}
                            {col.precision && col.scale && `(${col.precision},${col.scale})`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={col.nullable ? "YES" : "NO"}
                            size="small"
                            color={col.nullable ? "default" : "warning"}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                            {col.default || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>{col.position}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Foreign Keys */}
              {schemaData.foreign_keys && schemaData.foreign_keys.length > 0 && (
                <>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                    Foreign Keys
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Column</TableCell>
                          <TableCell>References Table</TableCell>
                          <TableCell>References Column</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {schemaData.foreign_keys.map((fk, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{fk.column}</TableCell>
                            <TableCell>{fk.references_table}</TableCell>
                            <TableCell>{fk.references_column}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Unique Constraints */}
              {schemaData.unique_constraints && schemaData.unique_constraints.length > 0 && (
                <>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                    Unique Constraints
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {schemaData.unique_constraints.map((uc, idx) => (
                      <Chip
                        key={idx}
                        label={`${uc.column} (${uc.constraint_name})`}
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                </>
              )}

              {/* Indexes */}
              {schemaData.indexes && schemaData.indexes.length > 0 && (
                <>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                    Indexes
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Definition</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {schemaData.indexes.map((idx) => (
                          <TableRow key={idx.name}>
                            <TableCell>
                              <Typography variant="body2" fontFamily="monospace">
                                {idx.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                                {idx.definition}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Raw JSON View */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  Raw JSON
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                      overflow: "auto",
                      maxHeight: "300px",
                      m: 0,
                    }}
                  >
                    {JSON.stringify(schemaData, null, 2)}
                  </Typography>
                </Paper>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSchemaDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create Organization Dialog */}
      <CreateOrganizationDialog
        open={createOrgDialogOpen}
        onClose={() => {
          setCreateOrgDialogOpen(false);
          setCreateError(null);
        }}
        formData={newOrgFormData}
        onFormDataChange={setNewOrgFormData}
        onCreate={handleCreateOrganization}
        creating={creatingOrg}
      />
    </>
  );
}
